import { lazy, Suspense, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { RequireAuth } from "./lib/auth.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import { shouldUseCarMode, setCarModePreference } from "./lib/device.js";

// Route-level code splitting: every page is its own chunk, so the initial
// bundle is just the shell + auth. Heavy libs (recharts, maps, pdf/canvas
// export) load only when a page that uses them is opened.
const LoginPage = lazy(() => import("./pages/LoginPage.jsx"));
const RegisterPage = lazy(() => import("./pages/RegisterPage.jsx"));
const DashboardPage = lazy(() => import("./pages/DashboardPage.jsx"));
const LandingPage = lazy(() => import("./pages/LandingPage.jsx"));
const VehicleDetailPage = lazy(() => import("./pages/VehicleDetailPage.jsx"));
const LinkApprovePage = lazy(() => import("./pages/LinkApprovePage.jsx"));
const PlansPage = lazy(() => import("./pages/PlansPage.jsx"));
const SettingsPage = lazy(() => import("./pages/SettingsPage.jsx"));
const AdminPage = lazy(() => import("./pages/AdminPage.jsx"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage.jsx"));
const DataPricingPage = lazy(() => import("./pages/DataPricingPage.jsx"));
const DataSuccessPage = lazy(() => import("./pages/DataSuccessPage.jsx"));
const MarketingPage = lazy(() => import("./pages/MarketingPage.jsx"));
const DemoEntry = lazy(() => import("./pages/DemoEntry.jsx"));
const CarPage = lazy(() => import("./pages/CarPage.jsx"));
const DashcamHelpPage = lazy(() => import("./pages/DashcamHelpPage.jsx"));

const Fallback = () => <div className="center-msg">Loading...</div>;

// Sends the Tesla center-screen browser to /car on first landing, and honours
// an explicit ?car=1 / ?car=0 override from any device so the preference can
// be set (or cleared) by URL. Only ever redirects from "/", so deep links and
// in-app navigation are never hijacked.
function CarModeGate() {
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const forced = q.get("car");
    if (forced === "1") {
      setCarModePreference("on");
      nav("/car", { replace: true });
      return;
    }
    if (forced === "0") {
      setCarModePreference("off");
      return;
    }
    if (loc.pathname !== "/") return;
    if (shouldUseCarMode()) nav("/car", { replace: true });
  }, [loc.pathname, loc.search, nav]);

  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <CarModeGate />
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/link/:token"
            element={
              <RequireAuth>
                <LinkApprovePage />
              </RequireAuth>
            }
          />
          <Route
            path="/plans"
            element={
              <RequireAuth ignoreLock>
                <PlansPage />
              </RequireAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <LandingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/car"
            element={
              <RequireAuth>
                <CarPage />
              </RequireAuth>
            }
          />
          <Route
            path="/overview"
            element={
              <RequireAuth>
                <DashboardPage />
              </RequireAuth>
            }
          />
          <Route
            path="/vehicles/:id"
            element={
              <RequireAuth>
                <VehicleDetailPage />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminPage />
              </RequireAuth>
            }
          />
          <Route
            path="/feedback"
            element={
              <RequireAuth>
                <FeedbackPage />
              </RequireAuth>
            }
          />
          <Route path="/demo" element={<DemoEntry />} />
          <Route path="/features" element={<MarketingPage page="features" />} />
          <Route path="/faq" element={<MarketingPage page="faq" />} />
          <Route path="/help/dashcam" element={<DashcamHelpPage />} />
          <Route path="/data/pricing" element={<DataPricingPage />} />
          <Route path="/data/success" element={<DataSuccessPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
